import * as React from 'react';
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { connect } from 'react-redux';
import { Col, ListGroup, ListGroupItem, Row } from 'react-bootstrap';
import { History } from 'history';
import { useHistory } from 'react-router-dom';
import { RootState } from '../ApplicationState';
import { isUserHasKey } from '../GlobalFunctions';
import ConfirmIrreversible from '../components/ConfirmIrreversible';
import ModalContainer from '../components/ModalContainer';
import GenericInformationModal from '../components/GenericInformationModal';
import AddStudents from './AddStudents';
import { resetIndividualPeekavilleProgress, resetClassPeekavilleProgress } from '../BackendInterface';
import { StudentListItem } from './StudentListItem';
import { EditStudentModal } from './EditStudentModal/EditStudentModal';
import EmptyClassMessage from './EmptyClassMessage/EmptyClassMessage';
import { selectClassroom } from '../Classrooms';
import { dismissForever, dismissTemporarily, isShouldShow } from '../core/core';
import Button from '../components/Button';
import ClassCodeButtons from './ClassCodeButtons';
import SwitchModeButton from './SwitchButton';
import SecondaryButton from '../components/SecondaryButton/SecondaryButton';
import ParentalConsentWarningModal from '../components/ParentalConsentWarningModal/ParentalConsentWarningModal';
import styles from '../SCSS/StudentList.module.scss';
import TextButton from '../components/TextButton/TextButton';
import { ClassroomType, StudentProxyType, UserProfileType } from '../../peekapak-types/DataProtocolTypes';
import SortDropdown from '../components/SortDropdown';

const sortDropdownArray = [
  { text: 'Default', value: 'default' },
  { text: 'First Name', value: 'firstName' },
  { text: 'Last Name', value: 'lastName' },
];

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
  classroom: selectClassroom(state),
});

const isMyPeekavilleClassroom = (classroom: ClassroomType) => {
  if (!classroom || !classroom.listOfStudentProxies) {
    return false;
  }

  const studentList = classroom.listOfStudentProxies;

  for (let i = 0; i < studentList.length; ++i) {
    if (studentList[i].userId) return true;
  }

  return false;
};

const handleOnMyPeekavilleUnits = (history: History) => {
  history.push('/myPeekavilleUnits');
};

const resetAllWarning =
  'Are you sure you want to reset myPeekaville quests for every student in this classroom? Students will keep their avatars, berries and completion data, but all myPeekaville quests will be reset. This action cannot be undone.';
const resetAllMessage = `To confirm, please type 'Reset Peekaville' below:`;
const resetIndividualWarning =
  'Are you sure you want to reset myPeekaville quests for this student? They will keep their avatar, berries, and completion data, but all myPeekaville quests will be reset. This action cannot be undone.';
const resetIndividualMessage = `To confirm, please type the student's name below:`;

const SidebarButtons = forwardRef(({ classroom, history, ...props }: Props, ref) => {
  const isMiddleSchool = () => {
    if (classroom.grade && classroom.grade > 5) return true;
    return false;
  };
  const CommonButtons = () => (
    <>
      {isMyPeekavilleClassroom(classroom) && !isMiddleSchool() && isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') && (
        <>
          <SecondaryButton id='downloadStudentInfoButton' onClick={props.onDownloadStudentInfo} small>
            myPeekaville Logins
          </SecondaryButton>
          <SecondaryButton
            id='myPeekavilleUnitsButton'
            onClick={(event) => handleOnMyPeekavilleUnits(history)}
            ref={ref}
            small
          >
            Unlock myPeekaville for Students
          </SecondaryButton>
          <Button small blueBorder onClick={props.handleOnResetClassPeekavilleProgress}>
            Reset MyPeekaville Quests
          </Button>
        </>
      )}
      {isMiddleSchool() && (
        <SecondaryButton id='downloadMiddleSchoolInfoButton' onClick={props.onDownloadStudentInfo} small>
          Print Student Logins
        </SecondaryButton>
      )}
    </>
  );

  return (
    <>
      <div className={styles.buttonSection}>
        <CommonButtons />
        {!isMiddleSchool() && <ClassCodeButtons {...{ ...props, classroom }} />}
        {!isMiddleSchool() && <SwitchModeButton {...{ ...props, classroom }} />}
      </div>
    </>
  );
});
SidebarButtons.displayName = 'SidebarButtons';

type Props = {
  innerRef: React.RefObject<HTMLDivElement>;
  handleOnResetClassPeekavilleProgress: (arg0: Event) => void;
  handleOnDefaultStudentPasswordChange: (arg0: Event) => void;
  readingLevels: Array<number>;
  onReplaceStudent: (arg0: Event) => void;
  classroom: ClassroomType;
  defaultStudentPassword: string;
  onAddStudents: (arg0: string[], arg1: string[]) => void;
  onConnectParents: (arg0: React.SyntheticEvent<HTMLButtonElement>) => void;
  onDeleteStudent: (arg0: Event) => void;
  onDownloadStudentInfo: (arg0: Event) => void;
  onUpdateAddStudentMode: (arg0: Event) => void;
  refToButton: {
    current: null | HTMLButtonElement;
  };
  studentList: StudentProxyType[];
  showAddNewStudent: boolean;
  userProfile: UserProfileType;
  history: History;
  totalNumberOfStudents: number | undefined;
  studentNumberLimit: number | undefined;
};
type State = {
  isShowAddStudentsModal: boolean;
  isShowEditStudentModal: boolean;
  isShowParentalConsentWarningModal: boolean;
  indexOfStudentUnderEdit: number;
  isShowResetIndividualPeekavilleModal: boolean;
  isShowResetAllPeekavilleModal: boolean;
  isShowConfirmResetModal: boolean;
  indexOfStudentToReset: number;
  sortBy: string;
  sortedStudentList: StudentProxyType[];
};

const NewStudentButton = (props: {
  handleOnNewStudent: (arg0: never) => void;
  totalNumberOfStudents: number | undefined;
  studentNumberLimit: number | undefined;
}) => {
  if (props.totalNumberOfStudents === undefined || props.studentNumberLimit === undefined) {
    return null;
  }

  const disabled = props.totalNumberOfStudents >= props.studentNumberLimit;
  const overlayTriggers = disabled ? ['hover', 'focus'] : [];

  return (
    <OverlayTrigger
      placement='right'
      delay={{ show: 250, hide: 400 }}
      trigger={overlayTriggers}
      overlay={<Tooltip id={styles.limitTooltip}>At or exceeded student number limit</Tooltip>}
    >
      <span id='disabled-workaround'>
        <TextButton inline blue underlineHover disabled={disabled} onClick={props.handleOnNewStudent}>
          + New Student
        </TextButton>
      </span>
    </OverlayTrigger>
  );
};

class StudentList extends React.Component<Props, State> {
  selectedStudentName: {} | undefined;
  constructor(props: Props) {
    super(props);
    this.state = {
      isShowAddStudentsModal: false,
      isShowEditStudentModal: false,
      isShowParentalConsentWarningModal: false,
      indexOfStudentUnderEdit: -1,
      isShowResetAllPeekavilleModal: false,
      isShowResetIndividualPeekavilleModal: false,
      isShowConfirmResetModal: false,
      indexOfStudentToReset: -1,
      sortBy: 'default',
      sortedStudentList: this.props.studentList,
    };
  }

  componentDidMount() {
    this.selectedStudentName = {};
    if (this.props.studentList && this.props.studentList.length > 0) {
      this.sortStudentList('default');
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.indexOfStudentUnderEdit !== prevState.indexOfStudentUnderEdit) {
      this.selectedStudentName = this.props.studentList[this.state.indexOfStudentUnderEdit];
    }
    if (prevProps.classroom !== this.props.classroom) {
      this.sortStudentList(this.state.sortBy);
    }
  }

  isMiddleSchool = (classroom: ClassroomType) => {
    if (classroom.grade && parseInt(classroom.grade) > 5) {
      return true;
    }
    return false;
  };

  handleOnEditStudent = (userId: string, event: React.SyntheticEvent) => {
    event.stopPropagation();
    this.selectedStudentName = this.props.studentList.find((student) => student.userId === userId);
    const indexOfStudentUnderEdit = this.props.studentList.findIndex((student) => student.userId === userId);

    this.setState({
      isShowEditStudentModal: true,
      indexOfStudentUnderEdit,
    });
  };
  handleOnResetStudentPeekavilleProgress = (userId: string, event: React.SyntheticEvent) => {
    event.stopPropagation();
    this.selectedStudentName = this.props.studentList.find((student) => student.userId === userId);
    const indexOfStudentToReset = this.props.studentList.findIndex((student) => student.userId === userId);
    this.setState({
      isShowResetIndividualPeekavilleModal: true,
      indexOfStudentToReset,
    });
  };
  onResetStudentProgress = async () => {
    try {
      await resetIndividualPeekavilleProgress(this.props.studentList[this.state.indexOfStudentToReset].userId);
    } finally {
      this.handleOnHideResetIndividualModal();
      this.setState({
        isShowConfirmResetModal: true,
      });
    }
  };

  handleOnResetClassPeekavilleProgress = () => {
    this.setState({
      isShowResetAllPeekavilleModal: true,
    });
  };

  onResetAllProgress = async () => {
    try {
      await resetClassPeekavilleProgress(this.props.classroom.classroomId);
    } finally {
      this.handleOnHideResetAllModal();
      this.setState({
        isShowConfirmResetModal: true,
      });
    }
  };

  handleOnNewStudent = () => {
    if (isShouldShow(this.props.userProfile, 'ParentalConsentWarning')) {
      this.setState({
        isShowParentalConsentWarningModal: true,
      });
    } else {
      this.setState({
        isShowAddStudentsModal: true,
      });
    }
  };
  onHideParentalConsentWarningModal = () =>
    this.setState({
      isShowParentalConsentWarningModal: false,
    });
  onAcknowledgeParentalConsentWarning = (dismissForeverFlag) => {
    if (dismissForeverFlag) {
      dismissForever('ParentalConsentWarning', this.props.userProfile.flags);
    } else {
      dismissTemporarily('ParentalConsentWarning');
    }

    this.onHideParentalConsentWarningModal();
    this.setState({
      isShowAddStudentsModal: true,
    });
  };
  handleOnHideNewStudentModal = () => {
    this.setState({
      isShowAddStudentsModal: false,
    });
  };
  handleOnHideEditStudentModal = () => {
    this.setState({
      isShowEditStudentModal: false,
    });
  };
  handleOnHideResetIndividualModal = () => {
    this.setState({
      isShowResetIndividualPeekavilleModal: false,
    });
  };
  handleOnHideResetAllModal = () => {
    this.setState({
      isShowResetAllPeekavilleModal: false,
    });
  };
  handleOnClickListItem = (event: React.SyntheticEvent<HTMLButtonElement, Event>) => {
    this.props.onConnectParents(event);
  };
  handleOnHideConfirmResetModal = () => {
    this.setState({ isShowConfirmResetModal: false });
  };

  handleDropdownChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;
    this.setState(() => ({ sortBy: value }));
    this.sortStudentList(value);
  };

  sortByFirstOrLastName = (list: StudentProxyType[], sortBy: string) => {
    const copy = [...list];

    return copy
      .filter((item) => item[sortBy])
      .sort((a, b) => {
        if (a[sortBy].localeCompare(b[sortBy]) !== 0) return a[sortBy].localeCompare(b[sortBy]);
        else {
          const aName = a.firstName + a.lastName;
          const bName = b.firstName + b.lastName;
          return aName.localeCompare(bName);
        }
      })
      .concat(copy.filter((item) => !item[sortBy]));
  };

  sortStudentList = async (value: string) => {
    try {
      if (!this.props.studentList) return;
      if (value === 'default') {
        this.setState(() => ({
          sortedStudentList: this.props.studentList,
        }));
      } else {
        this.setState(() => ({
          sortedStudentList: this.sortByFirstOrLastName(this.props.studentList, value),
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  render() {
    return (
      <>
        {this.props.studentList && this.props.studentList.length > 0 ? (
          <div className={styles.listAndButtonsSection}>
            <div className={styles.studentListContainer}>
              <SortDropdown
                className={styles.sortDropdown}
                options={sortDropdownArray}
                value={this.state.sortBy}
                onChange={(e) => this.handleDropdownChange(e)}
              />
              <ListGroup className={styles.StudentListGroup}>
                {this.state.sortedStudentList &&
                  this.state.sortedStudentList.map((student, index) => (
                    <StudentListItem
                      key={index}
                      itemIndex={index}
                      userId={student.userId}
                      onClick={this.handleOnClickListItem}
                      onDeleteStudent={this.props.onDeleteStudent}
                      onEditStudent={this.handleOnEditStudent}
                      onResetPeekaville={this.handleOnResetStudentPeekavilleProgress}
                      student={student}
                      isMiddleSchool={this.isMiddleSchool(this.props.classroom)}
                    />
                  ))}
                {this.props.showAddNewStudent && (
                  <ListGroupItem className={`${styles.StudentListItem} ${styles['bottom-item']}`}>
                    <Row>
                      <Col xs={12}>
                        <NewStudentButton
                          handleOnNewStudent={this.handleOnNewStudent}
                          totalNumberOfStudents={this.props.totalNumberOfStudents}
                          studentNumberLimit={this.props.studentNumberLimit}
                        />
                      </Col>
                    </Row>
                  </ListGroupItem>
                )}
              </ListGroup>
            </div>
            <SidebarButtons
              {...this.props}
              handleOnResetClassPeekavilleProgress={this.handleOnResetClassPeekavilleProgress}
              ref={this.props.innerRef}
            />
          </div>
        ) : (
          <EmptyClassMessage
            onAddStudents={this.handleOnNewStudent}
            onUpdateAddStudentMode={this.props.onUpdateAddStudentMode}
            isMiddleSchool={this.isMiddleSchool(this.props.classroom)}
          />
        )}
        {this.state.isShowAddStudentsModal && (
          <ModalContainer hideModal={this.handleOnHideNewStudentModal}>
            <AddStudents
              hideModal={this.handleOnHideNewStudentModal}
              defaultStudentPassword={this.props.defaultStudentPassword}
              studentNumberLimit={this.props.studentNumberLimit || 10000}
              totalNumberOfStudents={this.props.totalNumberOfStudents || 0}
              onAddStudents={this.props.onAddStudents}
            />
          </ModalContainer>
        )}
        <EditStudentModal
          indexOfStudentUnderEdit={this.state.indexOfStudentUnderEdit}
          onHide={this.handleOnHideEditStudentModal}
          onReplaceStudent={this.props.onReplaceStudent}
          show={this.state.isShowEditStudentModal}
          student={this.selectedStudentName}
          readingLevels={this.props.readingLevels}
        />
        <ParentalConsentWarningModal
          isShow={this.state.isShowParentalConsentWarningModal}
          onHide={this.onHideParentalConsentWarningModal}
          onAcknowledge={this.onAcknowledgeParentalConsentWarning}
        />
        {this.state.isShowResetIndividualPeekavilleModal && (
          <ModalContainer hideModal={this.handleOnHideResetIndividualModal}>
            <ConfirmIrreversible
              hideModal={this.handleOnHideResetIndividualModal}
              title='Reset Peekaville Quests'
              warningMessage={resetIndividualWarning}
              instructions={resetIndividualMessage}
              confirmTestString={`${this.selectedStudentName.firstName} ${
                this.selectedStudentName.lastname ?? ''
              }`.trim()}
              performIrreversibleAction={this.onResetStudentProgress}
            />
          </ModalContainer>
        )}
        {this.state.isShowResetAllPeekavilleModal && (
          <ModalContainer hideModal={this.handleOnHideResetAllModal}>
            <ConfirmIrreversible
              hideModal={this.handleOnHideResetAllModal}
              title='Reset Class Peekaville Quests'
              warningMessage={resetAllWarning}
              instructions={resetAllMessage}
              confirmTestString={'Reset Peekaville'}
              performIrreversibleAction={this.onResetAllProgress}
            />
          </ModalContainer>
        )}
        {this.state.isShowConfirmResetModal && (
          <ModalContainer hideModal={this.handleOnHideConfirmResetModal}>
            <GenericInformationModal
              hideModal={this.handleOnHideConfirmResetModal}
              title='Reset Complete'
              message='Student quests have been reset. This may take a few minutes to take effect in the myPeekaville game.'
            />
          </ModalContainer>
        )}
      </>
    );
  }
}

StudentList.propTypes = {
  onConnectParents: PropTypes.func.isRequired,
  studentList: PropTypes.arrayOf(PropTypes.object),
  onAddStudents: PropTypes.func.isRequired,
  onDeleteStudent: PropTypes.func.isRequired,
};

function ClassFunctionWrapper(props) {
  const history = useHistory();
  return <StudentList {...props} history={history} />;
}

export default connect(mapStateToProps, null)(ClassFunctionWrapper);
